/**
 * Error monitoring (Sentry) for the web apps.
 *
 * Privacy contract: what people say to Rob never leaves for Sentry. We
 * report exceptions with coarse tags, drop console/input breadcrumbs (they
 * could carry typed text), never attach request bodies, and identify users
 * only by their pseudonymous Firebase uid. No session replay.
 *
 * Localhost is excluded so development noise never reaches the dashboard.
 */

import * as Sentry from '@sentry/react';

const DSN = 'https://e7536470f43ceff2e3a9992f17cdfd24@o4511221850308608.ingest.us.sentry.io/4512035197616128';

const host = typeof window !== 'undefined' ? window.location.hostname : '';
const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '';

function environmentFor(hostname) {
  if (hostname.endsWith('askpastorrob.com')) return 'production';
  if (hostname.endsWith('.web.app') || hostname.endsWith('.firebaseapp.com')) return 'production-preview';
  return 'other';
}

let started = false;

export function initMonitoring(appName) {
  if (started || isLocal) return false;
  try {
    Sentry.init({
      dsn: DSN,
      environment: environmentFor(host),
      release: typeof __BUILD_ID__ !== 'undefined' ? `${appName}@${__BUILD_ID__}` : undefined,
      sendDefaultPii: false,
      // Errors only: no performance tracing (keeps the bundle small on phones
      // and never injects trace headers into our streaming function calls).
      tracesSampleRate: 0,
      ignoreErrors: [
        'ResizeObserver loop',
        'AbortError',
        'The operation was aborted',
        'signal is aborted without reason',
      ],
      beforeBreadcrumb(crumb, hint) {
        if (crumb.category === 'console' || crumb.category === 'ui.input') return null;
        // Zed's panel (src/components/ZedPanel.js) holds conversations about what visitors asked:
        // a click inside it is never a breadcrumb (Sentry records a clicked element's words).
        if (crumb.category === 'ui.click') {
          const target = hint && hint.event && hint.event.target;
          if (target && typeof target.closest === 'function' && target.closest('[data-zed]')) return null;
        }
        return crumb;
      },
      beforeSend(event) {
        if (event.request) {
          delete event.request.data;
          delete event.request.cookies;
        }
        return event;
      },
    });
    Sentry.setTag('app', appName);
    started = true;
  } catch (err) {
    console.warn('Monitoring init failed:', err);
  }
  return started;
}

export function setMonitoringUser(user) {
  if (!started) return;
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({ id: user.uid, segment: user.isAnonymous ? 'guest' : 'member' });
}

/**
 * Log + report a handled error. `context.where` names the code path;
 * tags/extras must stay coarse (never message text).
 */
export function reportError(err, context = {}) {
  const label = context.where || 'error';
  console.error(`[${label}]`, err);
  if (!started) return;
  if (err && (err.name === 'AbortError' || err.message === 'aborted')) return;
  try {
    Sentry.withScope((scope) => {
      scope.setTag('where', label);
      if (context.tags) scope.setTags(context.tags);
      if (context.extras) scope.setExtras(context.extras);
      Sentry.captureException(err instanceof Error ? err : new Error(String(err)));
    });
  } catch (reportErr) {
    // Monitoring must never break the app.
  }
}

export { Sentry };
